import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Filter, X } from "lucide-react";

interface DirectorySidebarProps {
  onFilterChange: (filters: FilterState) => void;
}

export interface FilterState {
  priceRange: [number, number];
  distance: number;
  features: string[];
  categories: string[];
  tags: string[];
}

const AVAILABLE_FEATURES = [
  "Piscina",
  "Sauna",
  "Parking",
  "Clases Grupales",
  "Entrenador Personal",
  "Área de Cardio",
  "Pesas Libres",
  "Spinning",
  "Yoga",
  "Pilates",
  "Crossfit",
];

const AVAILABLE_CATEGORIES = [
  "Gimnasio",
  "Fitness",
  "Centro de Yoga",
  "Pilates",
  "CrossFit",
  "Artes Marciales",
  "Boxeo",
  "Ciclismo",
  "Natación",
];

const AVAILABLE_TAGS = [
  "24 horas",
  "abierto 24h",
  "Clases grupales",
  "entrenadores profesionales",
  "Spinning",
  "Yoga",
  "Pilates",
  "CrossFit",
  "Piscina",
  "Sauna",
  "Parking",
  "actividades para niños",
  "actividades deportivas",
  "Entrenador Personal",
];

export const DirectorySidebar = ({ onFilterChange }: DirectorySidebarProps) => {
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 100]);
  const [distance, setDistance] = useState(50);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const handleFeatureToggle = (feature: string) => {
    const updated = selectedFeatures.includes(feature)
      ? selectedFeatures.filter(f => f !== feature)
      : [...selectedFeatures, feature];
    setSelectedFeatures(updated);
    applyFilters({ features: updated });
  };

  const handleCategoryToggle = (category: string) => {
    const updated = selectedCategories.includes(category)
      ? selectedCategories.filter(c => c !== category)
      : [...selectedCategories, category];
    setSelectedCategories(updated);
    applyFilters({ categories: updated });
  };

  const handleTagToggle = (tag: string) => {
    const updated = selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag];
    setSelectedTags(updated);
    applyFilters({ tags: updated });
  };

  const handlePriceChange = (value: number[]) => {
    const newRange: [number, number] = [value[0], value[1]];
    setPriceRange(newRange);
  };

  const handleDistanceChange = (value: string) => {
    const newDistance = parseInt(value);
    setDistance(newDistance);
    applyFilters({ distance: newDistance });
  };

  const applyFilters = (updates?: Partial<FilterState>) => {
    onFilterChange({
      priceRange: updates?.priceRange || priceRange,
      distance: updates?.distance || distance,
      features: updates?.features || selectedFeatures,
      categories: updates?.categories || selectedCategories,
      tags: updates?.tags || selectedTags,
    });
  };

  const clearAllFilters = () => {
    setPriceRange([0, 100]);
    setDistance(50);
    setSelectedFeatures([]);
    setSelectedCategories([]);
    setSelectedTags([]);
    onFilterChange({
      priceRange: [0, 100],
      distance: 50,
      features: [],
      categories: [],
      tags: [],
    });
  };

  const hasActiveFilters = 
    priceRange[0] !== 0 || 
    priceRange[1] !== 100 || 
    distance !== 50 || 
    selectedFeatures.length > 0 || 
    selectedCategories.length > 0 ||
    selectedTags.length > 0;

  return (
    <Card className="w-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filtros
          </CardTitle>
          {hasActiveFilters && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearAllFilters}
              className="h-8 px-2"
            >
              <X className="w-4 h-4 mr-1" />
              Limpiar
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Price Range */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Precio Mensual (€)</Label>
            <div className="px-2">
              <Slider
                min={0}
                max={100}
                step={5}
                value={priceRange}
                onValueChange={handlePriceChange}
                onValueCommit={() => applyFilters()}
                className="w-full"
              />
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>{priceRange[0]}€</span>
              <span>{priceRange[1] === 100 ? '100€+' : `${priceRange[1]}€`}</span>
            </div>
          </div>

          {/* Distance */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Distancia Máxima</Label>
            <Select value={distance.toString()} onValueChange={handleDistanceChange}>
              <SelectTrigger className="bg-background">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-background z-50">
                <SelectItem value="5">5 km</SelectItem>
                <SelectItem value="10">10 km</SelectItem>
                <SelectItem value="25">25 km</SelectItem>
                <SelectItem value="50">50 km</SelectItem>
                <SelectItem value="100">100 km</SelectItem>
                <SelectItem value="999">Cualquier distancia</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Categories */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Categorías</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {AVAILABLE_CATEGORIES.map((category) => (
                <div key={category} className="flex items-center space-x-2">
                  <Checkbox
                    id={`cat-${category}`}
                    checked={selectedCategories.includes(category)}
                    onCheckedChange={() => handleCategoryToggle(category)}
                  />
                  <label
                    htmlFor={`cat-${category}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    {category}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Features */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Instalaciones</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {AVAILABLE_FEATURES.map((feature) => (
                <div key={feature} className="flex items-center space-x-2">
                  <Checkbox
                    id={`feat-${feature}`}
                    checked={selectedFeatures.includes(feature)}
                    onCheckedChange={() => handleFeatureToggle(feature)}
                  />
                  <label
                    htmlFor={`feat-${feature}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    {feature}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold">Etiquetas</Label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {AVAILABLE_TAGS.map((tag) => (
                <div key={tag} className="flex items-center space-x-2">
                  <Checkbox
                    id={`tag-${tag}`}
                    checked={selectedTags.includes(tag)}
                    onCheckedChange={() => handleTagToggle(tag)}
                  />
                  <label
                    htmlFor={`tag-${tag}`}
                    className="text-sm cursor-pointer flex-1"
                  >
                    {tag}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
